import classNames from 'classnames';
import * as React from 'react';
import { useIntl } from 'react-intl';
import StarRating from '../../../../../../atoms/icons/StarRating';
import MerchantTag from '../../../../../../molecules/merchant/common/tags/MerchantTag';
import LowestPrice from '../../../../../../molecules/merchant/common/texts/LowestPrice';
import { BookingPageTagName } from '../../../../../../domain/entities/BookingPage';
import MerchantCourseDetailsReservationButton from '../../../../../../molecules/merchant/resources/book/MerchantCourseDetailsReservationButton';

import css from './MerchantCourseDetailsHeader.module.scss';
import { MerchantSetting } from '../../../../../../interfaces/merchants';
import { RSButtonLink } from '../../../../../../reservation-page/RSButtonLink';
import KantanIcon from '../../../../../../reservation-page/icons/kantan-icon.svg';
import { useUserMe } from '../../../../../../hooks/api/me';
import { RSKantanIconButtonAnchor } from '../../../../../../reservation-page/RSKantanIconButtonAnchor';
import { useRouter } from 'next/router';

export interface OwnProps {
  merchantSetting: MerchantSetting;
  isBookable: boolean;
  isEvent: boolean;
  isFullyBooked: boolean;
  name: string;
  nickname: string;
  merchant_public_id: string;
  public_id: string;
  tag_names: BookingPageTagName[];
  rate_count: number;
  rate?: number;
  lowest_price?: number;
  unique_pricing: boolean;
  optIn: {
    reservationFlow: {
      m3: boolean;
    };
  };
  isRequireCustomerLogin: boolean;
}

type Props = OwnProps;

const MerchantCourseDetailsHeader: React.FC<Props> = (props) => {
  const {
    merchantSetting,
    isFullyBooked,
    name,
    tag_names,
    rate_count,
    rate,
    lowest_price,
    unique_pricing,
    optIn,
    isEvent,
    merchant_public_id,
    public_id: resource_public_id,
    isRequireCustomerLogin,
  } = props;
  const classes = classNames(css.MerchantCourseDetailsHeader);
  const intl = useIntl();
  const { user } = useUserMe();

  const router = useRouter();

  const M3ReservationFlowQuery = React.useMemo(() => {
    const query = new URLSearchParams();
    router.query.selected_slot &&
      query.set('timeSlotId', router.query.selected_slot as string);
    return query.toString();
  }, [router.query.selected_slot]);

  return (
    <div className={classes}>
      <div className={css.MerchantCourseDetailsHeader__Inner}>
        {tag_names.map((tag, i) => (
          <MerchantTag key={i} status={tag} />
        ))}
        <div className={css.MerchantCourseDetailsHeader__Header}>
          <h1 className={css.MerchantCourseDetailsHeader__Title}>{name}</h1>
          <div className={css.MerchantCourseDetailsHeader__SummaryContainer}>
            <div className={css.MerchantCourseDetailsHeader__Summary}>
              <span>
                {merchantSetting.is_customer_review_active && (
                  <>
                    <StarRating
                      className={css.MerchantCourseDetailsHeader__StarRating}
                      rate={rate || 0}
                    />
                    {rate_count > 0 && (
                      <span
                        className={css.MerchantCourseDetailsHeader__ReviewCount}
                      >
                        {`(${rate_count})`}
                      </span>
                    )}
                  </>
                )}
              </span>
              {lowest_price !== undefined && !optIn.reservationFlow.m3 && (
                <LowestPrice
                  className={css.MerchantCourseDetailsHeader__Price}
                  lowestPrice={lowest_price}
                  uniquePricing={unique_pricing}
                />
              )}
            </div>

            <div className={css.MerchantCourseDetailsHeader__ButtonContainer}>
              {optIn.reservationFlow.m3 && isEvent ? (
                <div className="hidden legacyPc:block">
                  {user ? (
                    <div className="w-[200px]">
                      <RSButtonLink
                        isBlock={true}
                        href={`/${merchant_public_id}/${resource_public_id}/book/event_type`}
                      >
                        予約
                      </RSButtonLink>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      {!isRequireCustomerLogin && (
                        <div className="w-[200px]">
                          <RSButtonLink
                            isBlock={true}
                            href={`/${merchant_public_id}/${resource_public_id}/book/event_type?${M3ReservationFlowQuery}`}
                          >
                            ゲスト予約
                          </RSButtonLink>
                        </div>
                      )}
                      <div className="w-[200px]">
                        <RSKantanIconButtonAnchor
                          icon={
                            <KantanIcon
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                            />
                          }
                          isBlock={true}
                          href={`/signin/user?return_to=/${merchant_public_id}/${resource_public_id}/book/event_type?${M3ReservationFlowQuery}`}
                          variant="black"
                        >
                          ログインして予約
                        </RSKantanIconButtonAnchor>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <MerchantCourseDetailsReservationButton
                  contactUrl={merchantSetting.contact_url}
                  {...props}
                />
              )}
            </div>
          </div>
        </div>
        {isFullyBooked && (
          <p className={css.MerchantCourseDetailsHeader__Notice}>
            {intl.formatMessage({
              id: 'merchants.bookingPages.notice.fullyBooked',
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default MerchantCourseDetailsHeader;
